if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.cart = document.querySelector('cart-drawer');
      this.errorSlot = this.querySelector('[data-product-form-error]');
      this._unsubscribeItemError = null;
      this._unsubscribeCartAdd = null;
      this._handleItemError = this._handleItemError.bind(this);
      this._handleCartAdd = this._handleCartAdd.bind(this);
    }

    connectedCallback() {
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      this._unsubscribeItemError = theme.pubsub.subscribe(
        theme.pubsub.events.CART_ITEM_ERROR,
        this._handleItemError
      );

      this._unsubscribeCartAdd = theme.pubsub.subscribe(
        theme.pubsub.events.CART_ADD,
        this._handleCartAdd
      );

      const persistedMsg = theme.cart?.errors?.getProductForm(this.variantId);
      if (persistedMsg) this._showError(persistedMsg);
    }

    disconnectedCallback() {
      this._unsubscribeItemError?.();
      this._unsubscribeCartAdd?.();
    }

    get variantId() {
      return this.form?.querySelector('[name="id"]')?.value;
    }

    _handleItemError({ variantId, message }) {
      if (!variantId || String(variantId) !== String(this.variantId)) return;
      this._showError(message);
    }

    _handleCartAdd({ items }) {
      const added = items?.find(i => String(i.variant_id) === String(this.variantId));
      if (added) this._dismissError();
    }

    _showError(message) {
      if (!this.errorSlot) return;
      this.errorSlot.textContent = message;
      this.errorSlot.removeAttribute('hidden');
    }

    _dismissError() {
      if (!this.errorSlot) return;
      this.errorSlot.textContent = '';
      this.errorSlot.setAttribute('hidden', '');
    }

    async onSubmit(event) {
      event.preventDefault();

      if (this.submitButton?.disabled) return;

      const recipientForm = this.form.querySelector('recipient-form');
      if (recipientForm?.reportValidity && !recipientForm.reportValidity()) return;

      theme.cart?.errors?.clearProductForm(this.variantId);
      this._dismissError();
      this.setLoading(true);
      this.cart?.setLoading(true);

      const id = this.variantId;
      const quantity = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10);
      const properties = this._getLineItemProperties();

      try {
        const item = properties ? { id, quantity, properties } : { id, quantity };
        const data = await theme.cart.add(item, { openDrawer: true });

        if (this.cart?.renderContents) {
          this.cart.renderContents(data);
        }
        this.cart?.setLoading(false);

        theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, {
          source: 'product-form',
          productVariantId: id,
          cartData: data,
          skipFetch: true,
        });
      } catch (error) {
        this.cart?.setLoading(false);
        this.cart?.close();
      } finally {
        this.setLoading(false);
      }
    }

    _getLineItemProperties() {
      const properties = {};
      const formData = new FormData(this.form);

      const collect = (name, value) => {
        const match = name.match(/^properties\[(.+)\]$/);
        if (!match || value === '') return;
        properties[match[1]] = value;
      };

      formData.forEach((value, name) => collect(name, value));

      this._unassociatedPropertyInputs().forEach((input) => {
        if (formData.has(input.name)) return;
        collect(input.name, input.value);
      });

      return Object.keys(properties).length ? properties : null;
    }

    _unassociatedPropertyInputs() {
      const formElements = new Set(this.form?.elements ?? []);

      return [...this.querySelectorAll('[name^="properties["]')].filter((input) => (
        !formElements.has(input) &&
        !input.disabled &&
        (!['checkbox', 'radio'].includes(input.type) || input.checked)
      ));
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;

      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('loading', isLoading);
    }
  }

  customElements.define('product-form', ProductForm);
}
